import { useContext, useEffect, useMemo, useState } from "react";
import {
  AddClientForm,
  AlertComponent,
  ModalComponent,
  NavBar,
} from "../../components";
import DataContext from "../../context/DataContext";
import { useFetch, usePermissions } from "../../hooks";
import { usePaginator } from "../../hooks/usePaginator";
import { ClientProps } from "../../interfaces/interfaces";
import AddBoxIcon from "@mui/icons-material/AddBox";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Container,
  Fade,
  IconButton,
  Paper,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  ListColumn,
  ListPageLayout,
} from "../../components/shared/ListPageLayout";

interface FetchResponse {
  success?: boolean;
  data: ClientProps[];
}

export const ClientPage = () => {
  const { paginationModel, setPaginationModel } = usePaginator("clients");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const fetchFilters = useMemo(() => ({ search }), [search]);
  const { data, error, isLoading, fetchData, pagination } =
    useFetch<FetchResponse>(
      "client-paginate",
      paginationModel,
      undefined,
      undefined,
      fetchFilters,
    );
  const [isModalOpen, setIsModalOpen] = useState<Boolean>(false);
  const [clientToEdit, setClientToEdit] = useState<ClientProps>({
    id: 0,
    name: "",
  });
  const [isCreateClient, setIsCreateClient] = useState(false);
  const { isSuccess, isError, onCreateClient, onEditClient } =
    useContext(DataContext);
  const { canManageClients } = usePermissions();

  const [tableData, setTableData] = useState<ClientProps[]>([]);

  useEffect(() => {
    data && data.data && setTableData(data.data);
  }, [data]);

  const columns: ListColumn<ClientProps>[] = [
    {
      key: "name",
      header: "Client",
      render: (r) => r.name,
      sortAccessor: (r) => r.name,
      width: "1fr",
    },
  ];

  return (
    <>
      <NavBar title="Clients" buttons={[]} />
      <Box
        sx={{
          marginTop: "80px",
          minHeight: "calc(100vh - 80px)",
          background:
            "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Fade in={true} timeout={600}>
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 2, md: 4 },
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(140, 150, 169, 0.1)",
                boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
              }}
            >
              <ListPageLayout<ClientProps>
                title="Clients"
                columns={columns}
                rows={tableData}
                getRowId={(r) => r.id}
                rowsLoading={isLoading}
                isError={Boolean(error)}
                errorMessage="Clients could not be loaded."
                searchValue={search}
                onSearchChange={(value) => setSearch(value.trim())}
                page={paginationModel.page}
                pageSize={paginationModel.pageSize}
                totalCount={Number(pagination?.totalRecords || 0)}
                onPageChange={(page) =>
                  setPaginationModel({ ...paginationModel, page })
                }
                onPageSizeChange={(pageSize) =>
                  setPaginationModel({ page: 0, pageSize })
                }
                toolbarActions={
                  canManageClients ? (
                    <Button
                      startIcon={<AddBoxIcon />}
                      onClick={() => {
                        setIsCreateClient(true);
                        setIsModalOpen(true);
                      }}
                    >
                      New
                    </Button>
                  ) : undefined
                }
                rowActions={(r) => (
                  <>
                    <Tooltip title="Detail">
                      <IconButton
                        onClick={() => navigate(`/client/${r.id}`)}
                        size="small"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canManageClients && (
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => {
                            setIsCreateClient(false);
                            setClientToEdit(r);
                            setIsModalOpen(true);
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}
              />
            </Paper>
          </Fade>
        </Container>
      </Box>
      {isModalOpen && (
        <ModalComponent
          modalContent={
            <AddClientForm
              value={!isCreateClient ? clientToEdit.name : null}
              onCancel={() => setIsModalOpen(false)}
              onEditClient={
                !isCreateClient
                  ? (values) => {
                      onEditClient(clientToEdit.id, values, undefined, () => {
                        fetchData();
                        setIsModalOpen(false);
                      });
                    }
                  : undefined
              }
              onAddClient={
                isCreateClient
                  ? (name) => {
                      onCreateClient(name, () => {
                        fetchData();
                        setIsModalOpen(false);
                        setIsCreateClient(false);
                      });
                    }
                  : undefined
              }
            />
          }
        />
      )}
      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
    </>
  );
};
